import {
  blockAverage,
  cellCenterX,
  cellCenterY,
  createField,
  sampleBilinear,
  type GridSpec,
  type ScalarField,
} from './grid.ts';
import type { GridPlan } from './resolution.ts';
import { initialSnowDepth, type InitialSnowParams } from './snowcover.ts';
import {
  addLayer,
  createColumns,
  depthField,
  Grain,
  sweField,
  type SnowColumns,
} from './snowpack.ts';
import { computeStability, type StabilityResult } from './stability.ts';
import type { StormResult } from './storm.ts';
import {
  runTimeline,
  type EventSpan,
  type TimelineEvent,
  type TimelineSnapshot,
} from './timeline.ts';
import { temperatureAt, type TemperatureProfile } from './temperature.ts';
import { placeWeakLayers, type WeakLayerSpec } from './weaklayers.ts';
import { computeTopography, type Topography } from './topography.ts';

/**
 * The simulation state for one selected area. Terrain fields live on the terrain grid;
 * snowpack fields live on the (coarser or equal) snowpack grid, with their own elevation and
 * topography derived by block-averaging — derived fields are computed on the grid they are
 * used on, never resampled after the fact.
 */
export interface TerrainState {
  plan: GridPlan;
  elevation: ScalarField;
  topo: Topography;
  minElevation: number;
  maxElevation: number;
  /** True bearing of grid north (deg), i.e. the UTM grid convergence at the area centre. */
  gridNorthBearing: number;
}

export interface Conditions {
  /** Default temperature profile for new events (every event carries its own). */
  temperature: TemperatureProfile;
  initialSnow: InitialSnowParams;
  /** Weak layers placed at the start of the timeline. */
  weakLayers: WeakLayerSpec[];
  /** The weather, event after event (see timeline.ts). */
  events: TimelineEvent[];
  /** Hour on the timeline the snowpack is evaluated at (0 … total duration). */
  evaluationHour: number;
  /**
   * Real time of hour 0 (ISO, UTC) when the timeline comes from observed weather (imported
   * conditions); absent for made-up scenarios. Only used for labelling.
   */
  startTime?: string;
}

export interface SnowpackState {
  spec: GridSpec;
  elevation: ScalarField;
  topo: Topography;
  columns: SnowColumns;
  /** Total snow depth (m). */
  depth: ScalarField;
  /** Snow water equivalent (kg/m² = mm). */
  swe: ScalarField;
  /**
   * Weather totals up to the evaluation hour: snowfall, wind change, rain summed over the storms
   * so far, the most recent wind field, and the mass budget. Null before any storm.
   */
  storm: StormResult | null;
  stability: StabilityResult;
  /** Air temperature (°C) per snowpack cell. */
  airTemp: Float32Array;
  /** Timeline hour the snowpack describes. */
  evaluationHour: number;
  /** Number of cells each weak layer (by id) was placed in. */
  weakLayersPlaced: Record<string, number>;
  timeline: {
    spans: EventSpan[];
    totalHours: number;
    /** Compact states through the whole timeline, for scrubbing (empty if not recorded). */
    snapshots: TimelineSnapshot[];
  };
}

/** Everything the snowpack simulation needs; plain data so it can be posted to a worker. */
export interface ScenarioInput {
  /** Elevation on the snowpack grid. */
  elevation: ScalarField;
  gridNorthBearing: number;
  conditions: Conditions;
  /**
   * Run the whole timeline recording snapshots for scrubbing (true), or stop at the evaluation
   * hour (false — faster when only the evaluation hour changed).
   */
  recordSnapshots?: boolean;
}

/** Existing snow is stamped as deposited this many hours before the scenario starts. */
const INITIAL_SNOW_AGE_HOURS = 720;

export function buildTerrain(
  plan: GridPlan,
  elevation: ScalarField,
  gridNorthBearing = 0,
): TerrainState {
  let minElevation = Infinity;
  let maxElevation = -Infinity;
  for (const v of elevation.data) {
    if (v < minElevation) minElevation = v;
    if (v > maxElevation) maxElevation = v;
  }
  return {
    plan,
    elevation,
    topo: computeTopography(elevation, gridNorthBearing),
    minElevation,
    maxElevation,
    gridNorthBearing,
  };
}

/** Elevation on the snowpack grid (block-averaged from the terrain grid). */
export function snowpackElevation(terrain: TerrainState): ScalarField {
  const spec = terrain.plan.snowpack;
  return spec.cellSize === terrain.elevation.spec.cellSize
    ? terrain.elevation
    : blockAverage(terrain.elevation, spec);
}

export function scenarioInput(terrain: TerrainState, conditions: Conditions): ScenarioInput {
  return {
    elevation: snowpackElevation(terrain),
    gridNorthBearing: terrain.gridNorthBearing,
    conditions,
  };
}

/**
 * Build the snowpack: existing snow as one old (rounded-grain) layer, weak layers, then the
 * weather timeline up to the evaluation hour, and finally stability. Pure and synchronous; the app runs
 * it in a worker.
 */
export function runScenario(
  input: ScenarioInput,
  onProgress?: (fraction: number) => void,
): SnowpackState {
  const { elevation, gridNorthBearing, conditions } = input;
  const { spec } = elevation;
  const topo = computeTopography(elevation, gridNorthBearing);
  const columns = createColumns(spec);
  const initial = initialSnowDepth(conditions.initialSnow, elevation, topo.slope);
  const rho = conditions.initialSnow.density;
  for (let i = 0; i < initial.data.length; i++) {
    addLayer(columns, i, initial.data[i]! * rho, rho, Grain.RG, -INITIAL_SNOW_AGE_HOURS);
  }
  const weakLayersPlaced = placeWeakLayers(columns, conditions.weakLayers, elevation, topo);
  const tl = runTimeline({
    columns,
    elevation,
    topo,
    initialSnow: conditions.initialSnow,
    gridNorthBearing,
    events: conditions.events,
    evaluationHour: conditions.evaluationHour,
    recordSnapshots: input.recordSnapshots ?? true,
    ...(onProgress ? { onProgress: (f: number) => onProgress(0.95 * f) } : {}),
  });
  const stability = computeStability(tl.columns, topo.slope, tl.airTemp, tl.evaluationHour);
  onProgress?.(1);
  return {
    spec,
    elevation,
    topo,
    columns: tl.columns,
    depth: depthField(tl.columns),
    swe: sweField(tl.columns),
    storm: tl.weather,
    stability,
    airTemp: tl.airTemp,
    evaluationHour: tl.evaluationHour,
    weakLayersPlaced,
    timeline: { spans: tl.spans, totalHours: tl.totalHours, snapshots: tl.snapshots },
  };
}

/** Snowpack field sampled (bilinear) at every terrain cell centre, for rendering. */
export function resampleToTerrain(field: ScalarField, terrain: GridSpec): ScalarField {
  if (sameGrid(field.spec, terrain)) return field;
  const out = createField(terrain);
  for (let r = 0; r < terrain.rows; r++) {
    const y = cellCenterY(terrain, r);
    for (let c = 0; c < terrain.cols; c++) {
      out.data[r * terrain.cols + c] = sampleBilinear(field, cellCenterX(terrain, c), y);
    }
  }
  return out;
}

/** Air temperature at every cell of an elevation field. */
export function temperatureField(profile: TemperatureProfile, elevation: ScalarField): ScalarField {
  const out = createField(elevation.spec);
  for (let i = 0; i < out.data.length; i++)
    out.data[i] = temperatureAt(profile, elevation.data[i]!);
  return out;
}

function sameGrid(a: GridSpec, b: GridSpec): boolean {
  return (
    a.cols === b.cols &&
    a.rows === b.rows &&
    a.cellSize === b.cellSize &&
    a.minX === b.minX &&
    a.maxY === b.maxY
  );
}
